import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Skeleton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import { Integracao } from '../../../../../../shared/services/api/c4-model/SistemasService';
import { EnumContainerTipo } from '../../../../../../shared/enums';
import { ContainerService } from '../../../../../../shared/services/api/c4-model/ContainerService';

type TipoContainer = {
  id: number;
  nome: string;
};

type AcessoContainerIntegracao = {
  id: string;
  integracaoId: string;
  containerOrigem: EnumContainerTipo;
  containerDestino: EnumContainerTipo;
  acao: string;
  meioAcesso: string;
  acessoComponentes: Array<{
    nome: string; // only nome for backend
  }>;
};

type Row = {
  id: string;
  origem: EnumContainerTipo;
  destino: EnumContainerTipo;
  acao: string;
  meioAcesso: string;
  acessoComponentes: Array<{
    id?: string; // optional for frontend display
    nome: string; // required for backend
  }>;
  isEditing?: boolean;
  isNew?: boolean;
};

type Props = {
  initialRows?: Integracao[];
  onAcessoContainersChange?: (acessos: AcessoContainerIntegracao[]) => void;
  isLoading?: boolean;
  tiposContainer?: any[];
  loadingTiposContainer?: boolean;
};

const convertToRows = (integracoes: Integracao[]): Row[] => {
  return integracoes.flatMap((integracao) =>
    integracao.acessoContainers.map((acesso) => ({
      id: acesso.id,
      origem: acesso.containerOrigem,
      destino: acesso.containerDestino,
      acao: acesso.acao,
      meioAcesso: acesso.meioAcesso,
      acessoComponentes: acesso.acessoComponentes || [],
    }))
  );
};

const convertToAcessoContainers = (
  rows: Row[]
): AcessoContainerIntegracao[] => {
  return rows.map((row) => ({
    id: row.id,
    integracaoId: '', // será preenchido no backend
    containerOrigem: row.origem,
    containerDestino: row.destino,
    acao: row.acao,
    meioAcesso: row.meioAcesso,
    acessoComponentes: (row.acessoComponentes || []).map((componente) => ({
      // Only send 'nome' to backend, strip out 'id'
      nome: componente.nome,
    })),
  }));
};

export const ListaAcessoContainer: React.FC<Props> = ({
  initialRows = [],
  onAcessoContainersChange,
  isLoading = false,
  tiposContainer = [],
  loadingTiposContainer = false,
}) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [editingValues, setEditingValues] = useState<Record<string, any>>({});

  const getTipoContainerNome = (id: number): string => {
    const tipo = tiposContainer.find((t: any) => Number(t.id) === Number(id));
    return tipo ? tipo.nome : `Tipo ${id}`;
  };

  useEffect(() => {
    if (initialRows.length > 0) {
      setRows(convertToRows(initialRows));
    } else {
      // When no initial rows are provided, start with empty array
      setRows([]);
    }
  }, [initialRows]);

  const handleEdit = (id: string) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;

    // Set this row as editing and store current values
    setRows(rows.map((r) => (r.id === id ? { ...r, isEditing: true } : r)));
    setEditingValues({
      ...editingValues,
      [id]: {
        origem: row.origem,
        destino: row.destino,
        acao: row.acao,
        meioAcesso: row.meioAcesso,
      },
    });
  };

  const handleCancelEdit = (id: string) => {
    // Cancel editing mode
    setRows(
      rows
        .map((r) => {
          if (r.id === id) {
            if (r.isNew) {
              // Remove new rows that weren't saved
              return null;
            }
            return { ...r, isEditing: false };
          }
          return r;
        })
        .filter(Boolean) as Row[]
    );

    // Clear editing value
    const newEditingValues = { ...editingValues };
    delete newEditingValues[id];
    setEditingValues(newEditingValues);
  };

  const handleSaveEdit = (id: string) => {
    const editingValue = editingValues[id];
    if (
      !editingValue ||
      editingValue.origem === undefined ||
      editingValue.destino === undefined ||
      !editingValue.acao?.trim() ||
      !editingValue.meioAcesso?.trim()
    ) {
      return; // Don't save incomplete values
    }

    // Update the row
    const newRows = rows.map((r) =>
      r.id === id
        ? {
          ...r,
          origem: editingValue.origem,
          destino: editingValue.destino,
          acao: editingValue.acao.trim(),
          meioAcesso: editingValue.meioAcesso.trim(),
          isEditing: false,
          isNew: false,
        }
        : r
    );

    setRows(newRows);

    // Clear editing value
    const newEditingValues = { ...editingValues };
    delete newEditingValues[id];
    setEditingValues(newEditingValues);

    // Notify parent component
    if (onAcessoContainersChange) {
      onAcessoContainersChange(convertToAcessoContainers(newRows));
    }
  };

  const handleDelete = (id: string) => {
    const newRows = rows.filter((r) => r.id !== id);
    setRows(newRows);

    // Clear any editing value for this row
    const newEditingValues = { ...editingValues };
    delete newEditingValues[id];
    setEditingValues(newEditingValues);

    if (onAcessoContainersChange) {
      onAcessoContainersChange(convertToAcessoContainers(newRows));
    }
  };

  const handleAddNew = () => {
    const newRow: Row = {
      id: `temp-new-${Date.now()}`,
      origem: 1, // FRONTEND
      destino: 2, // BACKEND
      acao: '',
      meioAcesso: '',
      acessoComponentes: [],
      isEditing: true,
      isNew: true,
    };

    setRows([...rows, newRow]);
    setEditingValues({
      ...editingValues,
      [newRow.id]: {
        origem: newRow.origem,
        destino: newRow.destino,
        acao: newRow.acao,
        meioAcesso: newRow.meioAcesso,
      },
    });
  };

  const handleEditingValueChange = (id: string, field: string, value: any) => {
    setEditingValues({
      ...editingValues,
      [id]: {
        ...editingValues[id],
        [field]: value,
      },
    });
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={4}>
      {isLoading ? (
        <Skeleton variant="rectangular" width={80} height={36} />
      ) : (
        <Button
          variant="contained"
          color="primary"
          sx={{ width: '80px' }}
          onClick={handleAddNew}
          startIcon={<AddIcon />}
        >
          Novo
        </Button>
      )}

      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={100}>Ações</TableCell>
              <TableCell>Origem</TableCell>
              <TableCell>Destino</TableCell>
              <TableCell>Ação</TableCell>
              <TableCell>Meio de acesso</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  {row.isEditing ? (
                    <>
                      <Tooltip title="Salvar">
                        <IconButton
                          size="small"
                          onClick={() => handleSaveEdit(row.id)}
                          color="primary"
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancelar">
                        <IconButton
                          size="small"
                          onClick={() => handleCancelEdit(row.id)}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(row.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(row.id)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
                <TableCell>
                  {row.isEditing ? (
                    <FormControl fullWidth size="small">
                      <Select
                        value={editingValues[row.id]?.origem ?? row.origem}
                        onChange={(e) =>
                          handleEditingValueChange(
                            row.id,
                            'origem',
                            Number((e.target as HTMLInputElement).value)
                          )
                        }
                      >
                        {tiposContainer.map((tipo) => (
                          <MenuItem key={tipo.id} value={Number(tipo.id)}>
                            {tipo.nome}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    getTipoContainerNome(row.origem)
                  )}
                </TableCell>
                <TableCell>
                  {row.isEditing ? (
                    <FormControl fullWidth size="small">
                      <Select
                        value={editingValues[row.id]?.destino ?? row.destino}
                        onChange={(e) =>
                          handleEditingValueChange(
                            row.id,
                            'destino',
                            Number((e.target as HTMLInputElement).value)
                          )
                        }
                      >
                        {tiposContainer.map((tipo) => (
                          <MenuItem key={tipo.id} value={Number(tipo.id)}>
                            {tipo.nome}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    getTipoContainerNome(row.destino)
                  )}
                </TableCell>
                <TableCell>
                  {row.isEditing ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={editingValues[row.id]?.acao || ''}
                      onChange={(e) =>
                        handleEditingValueChange(row.id, 'acao', e.target.value)
                      }
                      placeholder="Ex: Consulta dados"
                    />
                  ) : (
                    row.acao
                  )}
                </TableCell>
                <TableCell>
                  {row.isEditing ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={editingValues[row.id]?.meioAcesso || ''}
                      onChange={(e) =>
                        handleEditingValueChange(
                          row.id,
                          'meioAcesso',
                          e.target.value
                        )
                      }
                      placeholder="Ex: HTTP REST/JSON"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSaveEdit(row.id);
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          handleCancelEdit(row.id);
                        }
                      }}
                    />
                  ) : (
                    row.meioAcesso
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
